import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useHome, useRecommended } from "../api/hooks";
import { MaskedLine, Reveal } from "../components/Motion";
import Marquee from "../components/Marquee";
import PhoneMockup from "../components/PhoneMockup";
import PlayStoreButton from "../components/PlayStoreButton";
import { OfferCard, BrandLogo } from "../components/OfferCard";
import { SectionHeader, OfferRowSkeleton, ErrorState, categoryIcon } from "../components/States";
import { Seo } from "../components/Seo";
import { useAuth } from "../auth/AuthContext";

const OfferRow = ({ eyebrow, title, offers, linkTo, testId }) => {
    if (!offers?.length) return null;
    return (
        <section data-testid={testId} className="py-10 sm:py-12">
            <Reveal>
                <SectionHeader eyebrow={eyebrow} title={title} linkTo={linkTo} testId={testId ? `${testId}-header` : undefined} />
            </Reveal>
            <div className="mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4">
                {offers.map((o) => (
                    <OfferCard key={o.id} offer={o} className="w-[270px] shrink-0 snap-start" />
                ))}
            </div>
        </section>
    );
};

const Hero = ({ categories }) => {
    const ref = useRef(null);
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const phoneY = useTransform(scrollYProgress, [0, 1], [0, 130]);
    const phoneRotate = useTransform(scrollYProgress, [0, 1], [0, -7]);

    const submit = (e) => {
        e.preventDefault();
        if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    };

    return (
        <section ref={ref} data-testid="hero-section" className="hero-glow relative overflow-hidden bg-ink text-cream">
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 pb-20 pt-36 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:pb-24 lg:pt-44">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.15, duration: 0.6 }}
                        className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist"
                        data-testid="hero-overline"
                    >
                        <span className="h-px w-10 bg-saffron" />
                        Live deals, coupons & AI search
                    </motion.p>
                    <h1
                        data-testid="hero-title"
                        className="mt-7 font-display text-5xl font-semibold leading-[1.04] tracking-tighter sm:text-6xl lg:text-[5.5rem]"
                    >
                        <MaskedLine delay={0.25}>Sahi Khoj.</MaskedLine>
                        <MaskedLine delay={0.4} className="text-saffron">
                            Sahi Daam.
                        </MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.7, ease: [0.33, 1, 0.68, 1] }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
                        data-testid="hero-description"
                    >
                        SahiKhoj helps you discover better prices, deals and coupons
                        across supported platforms — now on the web, and inside the
                        mobile app.
                    </motion.p>
                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.7, ease: [0.33, 1, 0.68, 1] }}
                        data-testid="hero-ai-search-form"
                        className="mt-9 flex max-w-xl items-center gap-2 rounded-full border border-cream/15 bg-cream/10 p-2 backdrop-blur-md transition-colors focus-within:border-saffron/60"
                    >
                        <Sparkles className="ml-3 h-5 w-5 shrink-0 text-saffron" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="hero-ai-search-input"
                            placeholder="Ask SahiKhoj AI..."
                            aria-label="Ask SahiKhoj AI"
                            className="w-full bg-transparent text-base text-cream placeholder:text-mist/60 focus:outline-none"
                        />
                        <button
                            type="submit"
                            data-testid="hero-ai-search-button"
                            className="shrink-0 rounded-full bg-saffron px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-saffron-hover"
                        >
                            Search
                        </button>
                    </motion.form>
                    <motion.button
                        type="button"
                        onClick={() => navigate("/search?q=Nike%20shoes%20under%20%E2%82%B92000")}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.1, duration: 0.7 }}
                        data-testid="hero-search-hint"
                        className="mt-4 text-left font-mono text-[11px] uppercase tracking-[0.18em] text-mist/70 transition-colors hover:text-saffron"
                    >
                        Try: “Nike shoes under ₹2000”
                    </motion.button>
                </div>
                <motion.div style={{ y: phoneY, rotate: phoneRotate }} className="relative lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, y: 60, scale: 0.94 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.55, duration: 1, ease: [0.33, 1, 0.68, 1] }}
                    >
                        <PhoneMockup />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

const CategoryChips = ({ categories }) => {
    if (!categories?.length) return null;
    return (
        <section data-testid="home-categories" className="mx-auto max-w-7xl px-6 pt-14 sm:px-8 lg:px-12">
            <Reveal>
                <div className="flex flex-wrap gap-3">
                    {categories.map((c) => {
                        const Icon = categoryIcon(c.icon);
                        return (
                            <Link
                                key={c.id}
                                to={`/categories/${c.slug}`}
                                data-testid={`category-chip-${c.slug}`}
                                className="group inline-flex items-center gap-2 rounded-full border border-ink/10 bg-white px-5 py-2.5 text-sm font-medium text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-saffron hover:text-saffron"
                            >
                                <Icon className="h-4 w-4 text-saffron" />
                                {c.name}
                            </Link>
                        );
                    })}
                </div>
            </Reveal>
        </section>
    );
};

const BrandGrid = ({ brands }) => {
    if (!brands?.length) return null;
    return (
        <section data-testid="home-popular-brands" className="mx-auto max-w-7xl px-6 py-10 sm:px-8 sm:py-12 lg:px-12">
            <Reveal>
                <SectionHeader eyebrow="Stores" title="Popular brands" linkTo="/brands" testId="home-brands-header" />
            </Reveal>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                {brands.slice(0, 12).map((b, i) => (
                    <Reveal key={b.id} delay={i * 0.04}>
                        <Link
                            to={`/brands/${b.slug}`}
                            data-testid={`brand-card-${b.slug}`}
                            className="group flex flex-col items-center gap-3 rounded-2xl border border-ink/5 bg-white p-6 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                        >
                            <BrandLogo brand={b} className="h-12 w-12" />
                            <span className="text-sm font-medium text-ink">{b.name}</span>
                            {b.offerCount != null && (
                                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-smoke">
                                    {b.offerCount} offer{b.offerCount === 1 ? "" : "s"}
                                </span>
                            )}
                        </Link>
                    </Reveal>
                ))}
            </div>
        </section>
    );
};

const RecommendedRow = () => {
    const { user } = useAuth();
    const { data } = useRecommended();
    if (!user || !data?.length) return null;
    return (
        <section data-testid="home-recommended" className="mt-10 bg-ink py-16 text-cream sm:py-20">
            <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                <Reveal>
                    <SectionHeader eyebrow="For you" title="Recommended for you" linkTo="/offers" dark testId="home-recommended-header" />
                </Reveal>
                <div className="mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4">
                    {data.map((o) => (
                        <OfferCard key={o.id} offer={o} className="w-[270px] shrink-0 snap-start" />
                    ))}
                </div>
            </div>
        </section>
    );
};

const HomePage = () => {
    const { data, isLoading, isError, refetch } = useHome();

    return (
        <>
            <Seo
                title="SahiKhoj – Sahi Khoj. Sahi Daam."
                description="SahiKhoj is a smart shopping and travel comparison app that helps users discover prices, deals, coupons and better-value options."
                path="/"
            />
            <Hero categories={data?.categories} />
            <Marquee />
            {isLoading && (
                <div className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:px-12" data-testid="home-loading">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="py-6">
                            <div className="h-8 w-52 animate-pulse rounded bg-ink/5" />
                            <OfferRowSkeleton />
                        </div>
                    ))}
                </div>
            )}
            {isError && (
                <div className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:px-12">
                    <ErrorState onRetry={refetch} />
                </div>
            )}
            {data && (
                <>
                    <CategoryChips categories={data.categories} />
                    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                        <OfferRow eyebrow="Handpicked" title="Featured offers" offers={data.featured} linkTo="/offers" testId="home-featured" />
                        <OfferRow eyebrow="Popular now" title="Trending" offers={data.trending} linkTo="/offers" testId="home-trending" />
                        <OfferRow eyebrow="Last chance" title="Ending soon" offers={data.endingSoon} linkTo="/offers" testId="home-ending-soon" />
                        <OfferRow eyebrow="Fresh in" title="Recently added" offers={data.recentlyAdded} linkTo="/offers" testId="home-recently-added" />
                        <OfferRow eyebrow="Max value" title="Biggest savings" offers={data.biggestSavings} linkTo="/deals" testId="home-biggest-savings" />
                    </div>
                    <RecommendedRow />
                    <BrandGrid brands={data.popularBrands} />
                </>
            )}
            <section id="download" data-testid="section-download" className="bg-cream px-6 pb-24 pt-6 sm:px-8 lg:px-12">
                <Reveal>
                    <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-india px-8 py-16 text-center sm:px-12 sm:py-20">
                        <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-leaf/30 blur-3xl" />
                        <div className="pointer-events-none absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-saffron/25 blur-3xl" />
                        <h2 className="mx-auto max-w-3xl font-display text-3xl font-semibold leading-[1.1] tracking-tight text-cream sm:text-4xl">
                            Everything here, plus price comparison, flights and hotels — in the app.
                        </h2>
                        <div className="mt-8 flex justify-center">
                            <PlayStoreButton testId="download-play-store-button" />
                        </div>
                        <p data-testid="download-placeholder-note" className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-cream/50">
                            Placeholder — Google Play link goes live at launch
                        </p>
                    </div>
                </Reveal>
            </section>
        </>
    );
};

export default HomePage;
